import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import GooglePlacesAutocompleteInput from "../../common/GooglePlacesAutocompleteInput";
import CareRichTextEditor from "../../components/care/CareRichTextEditor";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { useAuth } from "../../contexts/AuthContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import useAppConfig from "../../hooks/access-control/useAppConfig";
import { useFileUpload } from "../../hooks/useFileUpload";
import careService from "../../services/careService";
import { addressFieldsFromPlaceDetails } from "../../utils/googlePlaces";

const bioFields = [
  ["firstName", "First name", "text"],
  ["middleName", "Middle name", "text"],
  ["lastName", "Last name", "text"],
  ["preferredName", "Preferred name", "text"],
  ["dateOfBirth", "Date of birth", "date"],
  ["socialSecurityNumber", "Social Security Number", "text"],
  ["email", "Email", "email"],
];

const sexOptions = [
  "Female",
  "Male",
  "Intersex",
  "Non-binary",
  "Transgender female",
  "Transgender male",
  "Two-Spirit",
  "Questioning",
  "Not listed",
  "Prefer not to say",
  "Unknown",
  "Other",
];
const codeStatusOptions = ["CPR", "DNR", "Full Code", "Comfort Care", "Unknown"];
const yesNoOptions = ["Yes", "No"];

const addressFields = [
  ["address", "Address"],
  ["city", "City"],
  ["county", "County"],
  ["state", "State"],
  ["country", "Country"],
  ["zip", "Zip Code"],
];

const addressTypeOptions = ["Home", "Office", "Business", "Permanent", "Other"];
const usStateOptions = [
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "District of Columbia",
  "Florida",
  "Georgia",
  "Hawaii",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming",
];
const usStateAbbreviations = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  DC: "District of Columbia",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
};
const phoneFields = [
  ["mobilePhone", "Mobile phone"],
  ["officePhone", "Office phone"],
  ["homePhone", "Home phone"],
];
const preferredPhoneOptions = [
  ["mobilePhone", "Mobile"],
  ["officePhone", "Office"],
  ["homePhone", "Home"],
];

const assessmentItems = [
  "Medication list and medication behaviors",
  "Diagnosis and medical history",
  "Care needs and supervision level",
  "Behavior, welfare, and safety notes",
  "Emergency contacts and PoA details",
];

const contactRelationshipOptions = [
  "Spouse",
  "Daughter",
  "Son",
  "Parent",
  "Sibling",
  "Grandchild",
  "Emergency Contact",
  "Power of Attorney",
  "Legal Guardian",
  "Friend",
  "Case Manager",
  "Social Worker / Case Manager",
  "Financial Worker",
  "Nurse Delegator",
  "Pharmacy",
  "Primary Care Doctor",
  "Specialist",
  "Other",
];

const insuranceFields = [
  ["medicareNumber", "Medicare Number"],
  ["medicaidNumber", "Medicaid Number"],
];

const admissionDetailsFields = [
  ["hospitalName", "Hospital name", "text"],
  ["hospitalContactName", "Contact person", "text"],
  ["hospitalPhone", "Phone", "tel"],
  ["hospitalEmail", "Email", "email"],
  ["admissionDate", "Admission date", "date"],
  ["dischargeDate", "Discharge date", "date"],
];

const priorFamilyHomeFields = [
  ["priorFamilyHomeName", "Family home name"],
  ["priorFamilyHomeContactName", "Contact person"],
  ["priorFamilyHomePhone", "Phone"],
  ["priorFamilyHomeEmail", "Email"],
  ["priorFamilyHomeYears", "Years"],
  ["priorFamilyHomeMonths", "Months"],
];

const priorCaregiverFields = [
  ["priorCaregiverName", "Name"],
  ["priorCaregiverRelationship", "Relationship"],
  ["priorCaregiverPhone", "Phone"],
  ["priorCaregiverEmail", "Email"],
];

const otherInsuranceFields = [
  ["provider", "Insurance provider"],
  ["policyNumber", "Member / policy number"],
  ["groupNumber", "Group number"],
  ["notes", "Notes"],
];

const signedDocumentDefaults = [
  "Admission agreement",
  "Resident rights and responsibilities",
  "House policies",
  "Privacy / HIPAA acknowledgement",
  "Medication administration policy",
  "Emergency evacuation plan",
  "Smoking policy",
  "Financial agreement",
];

const intakeSteps = [
  ["source", "Source", "Choose manual entry or state assessment import"],
  ["bio", "Bio Data", "Resident identity, address, and demographics"],
  ["contacts", "Contacts", "Family, PoA, guardian, and emergency contacts"],
  ["careHistory", "Care History", "Admission source and previous care details"],
  ["care", "Care Snapshot", "Diagnosis, medications, care needs, and safety"],
  ["documents", "Documents", "Upload files and validate signed policies"],
  ["review", "Review", "Confirm and submit resident intake"],
];

const NEW_RESIDENT_DRAFT_KEY = "olreadia-care:new-resident-draft";

const onboardingStepStatusFor = (stepIndex = 0, savedAt = new Date().toISOString(), statusOverride = "") => {
  const safeIndex = Math.max(0, Math.min(stepIndex, intakeSteps.length - 1));
  const current = intakeSteps[safeIndex];
  const status =
    statusOverride ||
    (safeIndex === 0 ? "NOT_STARTED" : safeIndex === intakeSteps.length - 1 ? "READY_TO_SUBMIT" : "IN_PROGRESS");
  return {
    status,
    currentStepIndex: safeIndex,
    currentStepKey: current[0],
    currentStepLabel: current[1],
    lastSavedStepIndex: safeIndex,
    lastSavedStepKey: current[0],
    lastSavedStepLabel: current[1],
    completedSteps: intakeSteps.slice(0, safeIndex).map(([key, label], index) => ({ index, key, label })),
    remainingSteps: intakeSteps.slice(safeIndex + 1).map(([key, label], offset) => ({
      index: safeIndex + offset + 1,
      key,
      label,
    })),
    canResume: safeIndex > 0 && status !== "SUBMITTED",
    lastSavedAt: savedAt,
  };
};

const contactField = (key, field) => `${key}${field[0].toUpperCase()}${field.slice(1)}`;

const addressFieldName = (prefix, field) => (prefix ? contactField(prefix, field) : field);

const preferredPhoneLabel = (value) => preferredPhoneOptions.find(([field]) => field === value)?.[1] || "";

const apiSexValue = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "male") return "MALE";
  if (normalized === "female") return "FEMALE";
  return value ? "OTHER" : null;
};

const yesNoBoolean = (value) => {
  if (value === "Yes") return true;
  if (value === "No") return false;
  return null;
};

const splitName = (name = "") => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return { firstName: "", middleName: "", lastName: "" };
  if (parts.length === 1) return { firstName: parts[0], middleName: "", lastName: "" };
  return {
    firstName: parts[0],
    middleName: parts.length > 2 ? parts.slice(1, -1).join(" ") : "",
    lastName: parts[parts.length - 1],
  };
};

const addressPayload = (formData, prefix = "", { includeAddressType = true } = {}) => {
  const fieldName = (field) => addressFieldName(prefix, field);
  const addressType = formData[fieldName("addressType")] || "";
  return {
    address: formData[fieldName("address")] || "",
    addressLine2: formData[fieldName("addressLine2")] || "",
    city: formData[fieldName("city")] || "",
    county: formData[fieldName("county")] || "",
    state: formData[fieldName("state")] || "",
    country: formData[fieldName("country")] || "",
    zipCode: formData[fieldName("zip")] || "",
    latitude: formData[fieldName("latitude")] || null,
    longitude: formData[fieldName("longitude")] || null,
    placeId: formData[fieldName("placeId")] || "",
    addressType: includeAddressType ? addressType : "",
    otherAddressType: includeAddressType && addressType === "Other" ? formData[fieldName("addressTypeOther")] || "" : "",
  };
};

const phonePayload = (formData, prefix = "") => {
  const fieldName = (field) => (prefix ? contactField(prefix, field) : field);
  return {
    mobile: formData[fieldName("mobilePhone")] || "",
    office: formData[fieldName("officePhone")] || "",
    home: formData[fieldName("homePhone")] || "",
    preferred: phoneValueFor(formData, prefix),
  };
};

const personPayload = ({ formData, name, prefix = "", relationship = "", email = "", notes = "" }) => {
  const parts = splitName(name);
  return {
    ...parts,
    relationship,
    email,
    phones: phonePayload(formData, prefix),
    address: addressPayload(formData, prefix),
    notes,
  };
};

const contactTypeFor = (relationship = "") => {
  const normalized = relationship.toLowerCase();
  if (normalized.includes("social worker") || normalized.includes("case manager")) return "SOCIAL_WORKER";
  if (normalized.includes("power of attorney")) return "POWER_OF_ATTORNEY";
  if (normalized.includes("guardian")) return "GUARDIAN";
  if (normalized.includes("emergency")) return "EMERGENCY_CONTACT";
  if (normalized.includes("pharmacy")) return "PHARMACY";
  if (normalized.includes("doctor") || normalized.includes("physician")) return "PRIMARY_CARE_DOCTOR";
  if (normalized.includes("specialist")) return "SPECIALIST";
  return "OTHER";
};

const phoneValueFor = (formData, prefix = "") => {
  const fieldName = (field) => (prefix ? contactField(prefix, field) : field);
  const preferredPhone = formData[fieldName("preferredPhone")];
  return (
    (preferredPhone ? formData[fieldName(preferredPhone)] : "") ||
    formData[fieldName("mobilePhone")] ||
    formData[fieldName("officePhone")] ||
    formData[fieldName("homePhone")] ||
    ""
  );
};

const yesNoFromBoolean = (value) => {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return "";
};

const applyAddressToForm = (target, address = {}, prefix = "") => {
  const fieldName = (field) => addressFieldName(prefix, field);
  target[fieldName("address")] = address.address || "";
  target[fieldName("addressLine2")] = address.addressLine2 || "";
  target[fieldName("city")] = address.city || "";
  target[fieldName("county")] = address.county || "";
  target[fieldName("state")] = address.state || "";
  target[fieldName("country")] = address.country || "";
  target[fieldName("zip")] = address.zipCode || address.zip || "";
  target[fieldName("latitude")] = address.latitude || "";
  target[fieldName("longitude")] = address.longitude || "";
  target[fieldName("placeId")] = address.placeId || "";
  target[fieldName("addressType")] = address.addressType || "";
  target[fieldName("addressTypeOther")] = address.otherAddressType || "";
};

const apiContactToForm = (contact = {}, index = 0) => {
  const prefix = `residentContactLoaded${index + 1}`;
  const formData = {
    [contactField(prefix, "name")]: [contact.firstName, contact.middleName, contact.lastName].filter(Boolean).join(" ") || contact.organizationName || "",
    [contactField(prefix, "email")]: contact.email || "",
    [contactField(prefix, "mobilePhone")]: contact.phones?.mobile || "",
    [contactField(prefix, "officePhone")]: contact.phones?.office || "",
    [contactField(prefix, "homePhone")]: contact.phones?.home || "",
    [contactField(prefix, "preferredPhone")]: preferredPhoneOptions.find(([field]) => contact.phones?.preferred === contact.phones?.[field.replace("Phone", "")])?.[0] || "",
  };
  applyAddressToForm(formData, contact.address, prefix);
  return {
    contact: {
      id: `loaded-contact-${index + 1}`,
      prefix,
      type: contact.relationship || "",
      notes: contact.notes || "",
    },
    formData,
  };
};

const hydrateDraftFromPreview = (preview = {}) => {
  const stages = preview.previewPayload?.onboardingStages || {};
  const bio = stages.bio || {};
  const sourceForm = bio.sourceForm || {};
  const formData = { ...(sourceForm.formData || {}) };
  const personal = bio.personalInformation || {};
  const coverage = bio.healthAndCoverage || {};
  formData.firstName = formData.firstName || personal.firstName || "";
  formData.middleName = formData.middleName || personal.middleName || "";
  formData.lastName = formData.lastName || personal.lastName || "";
  formData.preferredName = formData.preferredName || personal.preferredName || "";
  formData.dateOfBirth = formData.dateOfBirth || personal.birthDate || "";
  formData.sex = formData.sex || (personal.sex ? personal.sex.charAt(0) + personal.sex.slice(1).toLowerCase() : "");
  formData.socialSecurityNumber = formData.socialSecurityNumber || personal.socialSecurityNumber || "";
  formData.primaryLanguage = formData.primaryLanguage || personal.primaryLanguage || "";
  formData.codeStatus = formData.codeStatus || coverage.codeStatus || "";
  formData.advancedDirectives = formData.advancedDirectives || yesNoFromBoolean(coverage.advancedDirectives);
  formData.medicareNumber = formData.medicareNumber || coverage.medicareNumber || "";
  formData.medicaidNumber = formData.medicaidNumber || coverage.medicaidNumber || "";
  formData.bioNotes = formData.bioNotes || bio.notes || bio.backgroundNotes || "";
  applyAddressToForm(formData, bio.address);

  const contactsPayload = stages.contacts?.contacts || [];
  const loadedContacts = contactsPayload.map(apiContactToForm);
  loadedContacts.forEach((entry) => Object.assign(formData, entry.formData));

  const careHistory = stages.careHistory?.careHistory || {};
  formData.wasAdmittedAtHospital = yesNoFromBoolean(careHistory.admittedAtHospital);
  if (careHistory.hospitalAdmission) {
    formData.hospitalName = careHistory.hospitalAdmission.name || "";
    formData.hospitalContactName = careHistory.hospitalAdmission.contactName || "";
    formData.hospitalPhone = careHistory.hospitalAdmission.phones?.preferred || careHistory.hospitalAdmission.phones?.mobile || "";
    formData.hospitalEmail = careHistory.hospitalAdmission.email || "";
    formData.admissionDescription = careHistory.hospitalAdmission.notes || "";
    applyAddressToForm(formData, careHistory.hospitalAdmission.address, "hospital");
  }
  formData.fromAnotherFamilyHome = yesNoFromBoolean(careHistory.comingFromAnotherFamilyHome);
  if (careHistory.previousFamilyHome) {
    formData.priorFamilyHomeName = careHistory.previousFamilyHome.name || "";
    formData.priorFamilyHomeContactName = careHistory.previousFamilyHome.contactName || "";
    formData.priorFamilyHomePhone = careHistory.previousFamilyHome.phones?.preferred || careHistory.previousFamilyHome.phones?.mobile || "";
    formData.priorFamilyHomeEmail = careHistory.previousFamilyHome.email || "";
    formData.priorFamilyHomeYears = careHistory.previousFamilyHome.years || "";
    formData.priorFamilyHomeMonths = careHistory.previousFamilyHome.months || "";
    formData.priorFamilyHomeDetails = careHistory.previousFamilyHome.notes || "";
    applyAddressToForm(formData, careHistory.previousFamilyHome.address, "priorFamilyHome");
  }
  formData.hadPriorCaregiver = yesNoFromBoolean(careHistory.hadPriorCaregiver);
  if (careHistory.priorCaregiver) {
    formData.priorCaregiverName = [careHistory.priorCaregiver.firstName, careHistory.priorCaregiver.middleName, careHistory.priorCaregiver.lastName].filter(Boolean).join(" ");
    formData.priorCaregiverRelationship = careHistory.priorCaregiver.relationship || "";
    formData.priorCaregiverPhone = careHistory.priorCaregiver.phones?.preferred || careHistory.priorCaregiver.phones?.mobile || "";
    formData.priorCaregiverEmail = careHistory.priorCaregiver.email || "";
    formData.priorCaregiverDetails = careHistory.priorCaregiver.notes || "";
    applyAddressToForm(formData, careHistory.priorCaregiver.address, "priorCaregiver");
  }

  const snapshot = stages.careSnapshot?.initialCareSnapshot || {};
  formData.diagnosis = snapshot.diagnosis || "";
  formData.allergies = snapshot.allergies || "";
  formData.medicationNotes = snapshot.medicationNotes || "";
  formData.careNeeded = snapshot.careNeeded || "";
  formData.behaviorSafetyNotes = snapshot.behaviorSafetyNotes || "";

  const documents = stages.documents?.documents || {};
  const uploadedDocuments = documents.uploadedDocuments || preview.uploadedDocuments || [];
  return {
    formData,
    contacts: loadedContacts.map((entry) => entry.contact),
    otherInsurances: sourceForm.otherInsurances || [],
    customCareQuestions: (snapshot.customQuestions || []).map((question, index) => ({
      id: `loaded-care-question-${index + 1}`,
      question: question.question || "",
      response: question.answer || question.response || "",
    })),
    documentFiles: uploadedDocuments.map((document, index) => ({
      id: `loaded-document-${index + 1}`,
      ...document,
      needsUpload: false,
      uploaded: document.uploaded ?? Boolean(document.fileId || document.storageFileId),
    })),
    signedDocumentValidations: (documents.signedDocumentValidations || []).map((document, index) => ({
      id: `loaded-signed-document-${index + 1}`,
      ...document,
    })),
    onboardingStepStatus: preview.onboardingStepStatus || preview.previewPayload?.onboardingStepStatus || onboardingStepStatusFor(1),
    onboardingResidentId: preview.residentId || "",
    onboardingRegistrationId: preview.registrationId || "",
    assessmentProcessId: preview.assessmentProcessId || preview.workflowId || "",
  };
};

const prefixedAddressSummary = (formData, prefix) => {
  const addressType =
    formData[contactField(prefix, "addressType")] === "Other"
      ? formData[contactField(prefix, "addressTypeOther")]
      : formData[contactField(prefix, "addressType")];
  const addressParts = ["address", "city", "county", "state", "country", "zip"]
    .map((field) => formData[contactField(prefix, field)])
    .filter(Boolean)
    .join(", ");
  return [addressType ? `Type: ${addressType}` : "", addressParts].filter(Boolean).join(" | ");
};

const documentHasStorageFile = (document) => Boolean(document?.fileId || document?.storageFileId);
const documentNeedsStorageUpload = (document) => Boolean(document?.needsUpload && document?.file && !documentHasStorageFile(document));
const documentPreviewUrl = (document) => document?.previewUrl || document?.signedUrl || document?.url || document?.localPreviewUrl || "";
const documentDownloadUrl = (document) => document?.downloadUrl || document?.signedUrl || document?.url || document?.previewUrl || document?.localPreviewUrl || "";
const persistedDocumentUrl = (url) => (url && !String(url).startsWith("blob:") ? url : "");
const uploadedDocumentsFromPayload = (payload = {}) => {
  const stages = payload?.onboardingStages || {};
  return (
    stages.documents?.documents?.uploadedDocuments ||
    payload?.documents?.documents?.uploadedDocuments ||
    payload?.documents?.uploadedDocuments ||
    payload?.uploadedDocuments ||
    []
  );
};
const mergeReturnedDocumentFiles = (currentDocuments, response) => {
  const returnedDocuments = uploadedDocumentsFromPayload(response?.payload || response?.previewPayload || {});
  if (!Array.isArray(returnedDocuments) || !returnedDocuments.length) {
    return currentDocuments;
  }
  const keyFor = (document) => String(document?.fileId || document?.storageFileId || document?.fileName || document?.name || "");
  const returnedByKey = new Map(returnedDocuments.map((document) => [keyFor(document), document]).filter(([key]) => key));
  return currentDocuments.map((document) => {
    const returnedDocument = returnedByKey.get(keyFor(document));
    if (!returnedDocument) {
      return document;
    }
    const signedUrl = returnedDocument.signedUrl || returnedDocument.previewUrl || returnedDocument.downloadUrl || document.signedUrl || "";
    return {
      ...document,
      ...returnedDocument,
      id: document.id,
      file: document.file,
      localPreviewUrl: document.localPreviewUrl,
      fileId: returnedDocument.fileId || returnedDocument.storageFileId || document.fileId || document.storageFileId || null,
      storageFileId: returnedDocument.storageFileId || returnedDocument.fileId || document.storageFileId || document.fileId || null,
      signedUrl,
      previewUrl: signedUrl || returnedDocument.previewUrl || document.previewUrl || "",
      downloadUrl: returnedDocument.downloadUrl || signedUrl || document.downloadUrl || "",
      needsUpload: false,
      uploaded: returnedDocument.uploaded ?? true,
    };
  });
};
const isImageDocument = (document) =>
  String(document?.type || document?.mimeType || "").startsWith("image/") ||
  /\.(png|jpe?g|gif|webp|heic)$/i.test(document?.fileName || document?.name || "");

const buildResidentRegistrationPayload = ({
  assessmentFile,
  assessmentFileMeta,
  assessmentImported,
  contacts,
  customCareQuestions,
  documentFiles,
  formData,
  onboardingStepStatus,
  otherInsurances,
  selectedWorkspace,
  selectedWorkspaceId,
  signedDocumentValidations,
}) => {
  const residentContacts = contacts.map((contact) => {
    const rawName = formData[contactField(contact.prefix, "name")] || "";
    const name = splitName(rawName);
    const contactType = contactTypeFor(contact.type || "");
    const isDecisionMaker = ["POWER_OF_ATTORNEY", "GUARDIAN"].includes(contactType);
    return {
      contactType,
      relationship: contact.type || "",
      ...name,
      organizationName: name.lastName ? "" : rawName,
      email: formData[contactField(contact.prefix, "email")] || "",
      phones: phonePayload(formData, contact.prefix),
      address: addressPayload(formData, contact.prefix),
      primaryContact: Boolean(!contacts.indexOf(contact)),
      emergencyContact: contactType === "EMERGENCY_CONTACT" || isDecisionMaker,
      decisionMaker: isDecisionMaker,
      nextOfKin: isDecisionMaker || Boolean(!contacts.indexOf(contact)),
      notes: contact.notes || "",
    };
  });

  return {
    resident: {
      workspaceId: selectedWorkspaceId || null,
      organizationId: selectedWorkspace?.organizationId || null,
      businessEntityId: null,
      businessLocationId: null,
      process: {
        patientType: "NEW",
        startDate: formData.processStartDate || formData.admissionDate || new Date().toISOString().slice(0, 10),
        endDate: formData.processEndDate || null,
        status: "PROSPECTIVE",
        existingResidentId: null,
      },
      personalInformation: {
        firstName: formData.firstName || "",
        middleName: formData.middleName || "",
        lastName: formData.lastName || "",
        preferredName: formData.preferredName || "",
        birthDate: formData.dateOfBirth || null,
        sex: apiSexValue(formData.sex),
        socialSecurityNumber: formData.socialSecurityNumber || "",
        primaryLanguage: formData.primaryLanguage || "",
      },
      healthAndCoverage: {
        codeStatus: formData.codeStatus || "",
        advancedDirectives: yesNoBoolean(formData.advancedDirectives),
        medicareNumber: formData.medicareNumber || "",
        medicaidNumber: formData.medicaidNumber || "",
        supplementaryInsurances: formData.hasOtherInsurance === "Yes" ? otherInsurances.map((insurance) => ({
          provider: insurance.provider || "",
          memberPolicyNumber: insurance.policyNumber || "",
          groupNumber: insurance.groupNumber || "",
          notes: insurance.notes || "",
        })) : [],
      },
      backgroundNotes: formData.bioNotes || "",
      notes: formData.bioNotes || "",
      onboardingStepStatus,
      address: addressPayload(formData),
      admission: {
        admissionDate: formData.admissionDate || null,
        dischargeDate: formData.dischargeDate || null,
        conditionDiagnosisAndCauseForAdmission: formData.admissionDescription || "",
      },
      contacts: residentContacts,
      careHistory: {
        admittedAtHospital: yesNoBoolean(formData.wasAdmittedAtHospital),
        hospitalAdmission: formData.wasAdmittedAtHospital === "Yes" ? {
          name: formData.hospitalName || "",
          contactName: formData.hospitalContactName || "",
          email: formData.hospitalEmail || "",
          phones: {
            mobile: formData.hospitalPhone || "",
            office: "",
            home: "",
            preferred: formData.hospitalPhone || "",
          },
          address: addressPayload(formData, "hospital", { includeAddressType: false }),
          notes: formData.admissionDescription || "",
        } : null,
        comingFromAnotherFamilyHome: yesNoBoolean(formData.fromAnotherFamilyHome),
        previousFamilyHome: formData.fromAnotherFamilyHome === "Yes" ? {
          name: formData.priorFamilyHomeName || "",
          contactName: formData.priorFamilyHomeContactName || "",
          email: formData.priorFamilyHomeEmail || "",
          phones: {
            mobile: formData.priorFamilyHomePhone || "",
            office: "",
            home: "",
            preferred: formData.priorFamilyHomePhone || "",
          },
          address: addressPayload(formData, "priorFamilyHome", { includeAddressType: false }),
          years: formData.priorFamilyHomeYears ? Number(formData.priorFamilyHomeYears) : null,
          months: formData.priorFamilyHomeMonths ? Number(formData.priorFamilyHomeMonths) : null,
          notes: formData.priorFamilyHomeDetails || "",
        } : null,
        hadPriorCaregiver: yesNoBoolean(formData.hadPriorCaregiver),
        priorCaregiver: formData.hadPriorCaregiver === "Yes"
          ? personPayload({
              formData,
              name: formData.priorCaregiverName || "",
              prefix: "priorCaregiver",
              relationship: formData.priorCaregiverRelationship || "",
              email: formData.priorCaregiverEmail || "",
              notes: formData.priorCaregiverDetails || "",
            })
          : null,
        notes: [formData.admissionDescription, formData.priorFamilyHomeDetails, formData.priorCaregiverDetails].filter(Boolean).join("\n\n"),
      },
      initialCareSnapshot: {
        conditionSummary: formData.admissionDescription || "",
        diagnosis: formData.diagnosis || "",
        allergies: formData.allergies || "",
        medicationNotes: formData.medicationNotes || "",
        careNeeded: formData.careNeeded || "",
        behaviorSafetyNotes: formData.behaviorSafetyNotes || "",
        customQuestions: customCareQuestions.map((question) => ({
          question: question.question || "",
          answer: question.response || "",
        })),
      },
      documents: {
        uploadedDocuments: documentFiles.map((document) => ({
          fileId: document.fileId || document.storageFileId || null,
          name: document.name || document.fileName || "",
          fileName: document.fileName || document.name || "",
          description: document.description || "",
          type: document.type || "",
          size: document.size || null,
          signedUrl: persistedDocumentUrl(document.signedUrl),
          previewUrl: persistedDocumentUrl(document.previewUrl),
          downloadUrl: persistedDocumentUrl(document.downloadUrl || document.signedUrl),
          uploaded: Boolean(document.uploaded),
          confirmed: Boolean(document.confirmed),
          uploadedAt: document.uploadedAt || null,
          storageStatus: document.storageStatus || (documentHasStorageFile(document) ? "CONFIRMED" : "PENDING_UPLOAD"),
        })),
        signedDocumentValidations: signedDocumentValidations.map((document) => ({
          documentName: document.documentName || "",
          signedVerified: Boolean(document.signedVerified),
          fileUploaded: Boolean(document.fileUploaded),
          notes: document.notes || "",
        })),
      },
      sourceForm: {
        formData,
        contacts,
        otherInsurances,
        customCareQuestions,
        documentFiles,
        onboardingStepStatus,
        signedDocumentValidations,
        assessmentFile: assessmentFile
          ? {
              name: assessmentFile.name,
              size: assessmentFile.size,
              type: assessmentFile.type,
              imported: assessmentImported,
            }
          : assessmentFileMeta
            ? { ...assessmentFileMeta, imported: assessmentImported }
            : null,
      },
    },
  };
};

const buildOnboardingStagePayloads = (options) => {
  const fullPayload = buildResidentRegistrationPayload(options);
  const resident = fullPayload.resident;
  const base = {
    workspaceId: resident.workspaceId,
    organizationId: resident.organizationId,
    onboardingStepStatus: resident.onboardingStepStatus,
  };
  return {
    bio: {
      resident: {
        workspaceId: resident.workspaceId,
        organizationId: resident.organizationId,
        businessEntityId: resident.businessEntityId,
        businessLocationId: resident.businessLocationId,
        process: resident.process,
        personalInformation: resident.personalInformation,
        healthAndCoverage: resident.healthAndCoverage,
        backgroundNotes: resident.backgroundNotes,
        notes: resident.notes,
        onboardingStepStatus: resident.onboardingStepStatus,
        address: resident.address,
        admission: resident.admission,
        sourceForm: {
          formData: resident.sourceForm.formData,
          otherInsurances: resident.sourceForm.otherInsurances,
          assessmentFile: resident.sourceForm.assessmentFile,
          onboardingStepStatus: resident.onboardingStepStatus,
        },
      },
    },
    contacts: {
      ...base,
      payload: {
        residentId: options.onboardingResidentId || null,
        contacts: resident.contacts,
      },
    },
    careHistory: {
      ...base,
      payload: {
        residentId: options.onboardingResidentId || null,
        careHistory: resident.careHistory,
      },
    },
    care: {
      ...base,
      payload: {
        residentId: options.onboardingResidentId || null,
        initialCareSnapshot: resident.initialCareSnapshot,
      },
    },
    documents: {
      ...base,
      payload: {
        residentId: options.onboardingResidentId || null,
        documents: resident.documents,
      },
    },
  };
};

const errorMessageFrom = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  "Could not submit resident. Please review the highlighted information and try again.";

const emptyDraft = {
  currentStep: 0,
  contacts: [],
  otherInsurances: [],
  customCareQuestions: [],
  documentFiles: [],
  onboardingStepStatus: onboardingStepStatusFor(0),
  onboardingResidentId: "",
  onboardingRegistrationId: "",
  assessmentProcessId: "",
  signedDocumentValidations: signedDocumentDefaults.map((documentName) => ({
    id: `signed-${documentName.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
    documentName,
    signedVerified: false,
    fileUploaded: false,
    notes: "",
  })),
  selectedContactId: "",
  assessmentFileMeta: null,
  assessmentImported: false,
  formData: {},
};

const formatSocialSecurityNumber = (value) => {
  const digits = value.replace(/\D/g, "").slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
};

const normalizeStateSelection = (value) => {
  if (!value) return "";
  if (usStateOptions.includes(value)) return value;
  return usStateAbbreviations[value.toUpperCase()] || "";
};

function ResidentAddressFields({ appConfigLoading, compact = false, formData, googleMapsApiKey, prefix = "", showAddressType = true, title = "Address", updateField }) {
  const fieldName = (field) => addressFieldName(prefix, field);
  const applyAddressFields = (addressFields) => {
    updateField(fieldName("address"), addressFields.addressLine1 || addressFields.formattedAddress || formData[fieldName("address")] || "");
    updateField(fieldName("city"), addressFields.city || "");
    updateField(fieldName("county"), addressFields.county || "");
    updateField(fieldName("state"), normalizeStateSelection(addressFields.state));
    updateField(fieldName("country"), addressFields.country || "");
    updateField(fieldName("zip"), addressFields.zipCode || "");
  };
  const searchField = fieldName("addressSearch");
  const addressField = fieldName("address");
  const addressTypeField = fieldName("addressType");
  const addressTypeOtherField = fieldName("addressTypeOther");
  const selectedAddressType = formData[addressTypeField] || "";

  return (
    <div className={`resident-address-section full-span${compact ? " is-compact" : ""}`}>
      <div className="resident-form-section-title">
        <i className="ti ti-map-pin" />
        <span>{title}</span>
      </div>
      <label className="resident-form-field full-span resident-address-search">
        <span>Location search</span>
        {appConfigLoading && !googleMapsApiKey ? (
          <input type="text" disabled placeholder="Loading map address search..." />
        ) : (
          <GooglePlacesAutocompleteInput
            googleMapsApiKey={googleMapsApiKey}
            placeholder={googleMapsApiKey ? "Search address e.g. 100 Main St, Seattle" : "Enter address"}
            types={["address"]}
            value={formData[searchField] || formData[addressField] || ""}
            onChange={(value) => {
              updateField(searchField, value);
              if (!googleMapsApiKey) updateField(addressField, value);
            }}
            onResolvePlace={async ({ details, prediction }) => {
              const resolvedAddress = details?.formatted_address || prediction?.description || formData[searchField] || "";
              updateField(searchField, resolvedAddress);
              updateField(addressField, resolvedAddress);
              if (details) {
                applyAddressFields({
                  ...addressFieldsFromPlaceDetails(details),
                  formattedAddress: resolvedAddress,
                });
              }
            }}
          />
        )}
      </label>
      <div className="resident-address-grid">
        {showAddressType ? (
          <label className="resident-form-field">
            <span>Address type</span>
            <select
              name={addressTypeField}
              value={selectedAddressType}
              onChange={(event) => updateField(addressTypeField, event.target.value)}
            >
              <option value="">Select address type</option>
              {addressTypeOptions.map((option) => (
                <option value={option} key={option}>{option}</option>
              ))}
            </select>
          </label>
        ) : null}
        {showAddressType && selectedAddressType === "Other" ? (
          <label className="resident-form-field">
            <span>Specify address type</span>
            <input
              name={addressTypeOtherField}
              type="text"
              value={formData[addressTypeOtherField] || ""}
              onChange={(event) => updateField(addressTypeOtherField, event.target.value)}
            />
          </label>
        ) : null}
        {addressFields.map(([field, label]) => (
          <label className={`resident-form-field resident-address-field resident-address-field-${field}`} key={field}>
            <span>{label}</span>
            {field === "state" ? (
              <select
                name={fieldName(field)}
                value={formData[fieldName(field)] || ""}
                onChange={(event) => updateField(fieldName(field), event.target.value)}
              >
                <option value="">Select state</option>
                {usStateOptions.map((option) => (
                  <option value={option} key={option}>{option}</option>
                ))}
              </select>
            ) : (
              <input
                name={fieldName(field)}
                type="text"
                value={formData[fieldName(field)] || ""}
                onChange={(event) => updateField(fieldName(field), event.target.value)}
              />
            )}
          </label>
        ))}
      </div>
    </div>
  );
}

const contactSummary = (formData, key) => {
  const addressType =
    formData[contactField(key, "addressType")] === "Other"
      ? formData[contactField(key, "addressTypeOther")]
      : formData[contactField(key, "addressType")];
  const addressParts = ["address", "city", "county", "state", "country", "zip"]
    .map((field) => formData[contactField(key, field)])
    .filter(Boolean)
    .join(", ");
  const values = [
    formData[contactField(key, "name")],
    formData[contactField(key, "mobilePhone")] ? `Mobile: ${formData[contactField(key, "mobilePhone")]}` : "",
    formData[contactField(key, "officePhone")] ? `Office: ${formData[contactField(key, "officePhone")]}` : "",
    formData[contactField(key, "homePhone")] ? `Home: ${formData[contactField(key, "homePhone")]}` : "",
    formData[contactField(key, "preferredPhone")] ? `Preferred: ${preferredPhoneLabel(formData[contactField(key, "preferredPhone")])}` : "",
    formData[contactField(key, "fax")] ? `Fax: ${formData[contactField(key, "fax")]}` : "",
    formData[contactField(key, "email")] ? `Email: ${formData[contactField(key, "email")]}` : "",
    addressType ? `Address type: ${addressType}` : "",
    addressParts ? `Address: ${addressParts}` : "",
  ].filter(Boolean);
  return values.join(" | ");
};

function FacesheetContactCard({ appConfigLoading, canRemove = true, contact, formData, googleMapsApiKey, onRemove, updateContact, updateField }) {
  const prefix = contact.prefix;
  return (
    <article className="resident-contact-card">
      <div className="resident-contact-card-title">
        <span>{contact.type || "New Contact"}</span>
        <button aria-label="Remove contact" disabled={!canRemove} type="button" onClick={() => onRemove(contact.id)}>
          <i className="ti ti-trash" />
        </button>
      </div>
      <div className="resident-contact-fields">
        <label className="resident-form-field">
          <span>Relationship</span>
          <select value={contact.type || ""} onChange={(event) => updateContact(contact.id, "type", event.target.value)}>
            <option value="">Select relationship</option>
            {contactRelationshipOptions.map((option) => (
              <option value={option} key={option}>{option}</option>
            ))}
          </select>
        </label>
        <label className="resident-form-field">
          <span>Name / organization</span>
          <input value={formData[contactField(prefix, "name")] || ""} onChange={(event) => updateField(contactField(prefix, "name"), event.target.value)} />
        </label>
        {phoneFields.map(([field, label]) => (
          <label className="resident-form-field" key={field}>
            <span>{label}</span>
            <input type="tel" value={formData[contactField(prefix, field)] || ""} onChange={(event) => updateField(contactField(prefix, field), event.target.value)} />
          </label>
        ))}
        <label className="resident-form-field">
          <span>Preferred phone</span>
          <select value={formData[contactField(prefix, "preferredPhone")] || ""} onChange={(event) => updateField(contactField(prefix, "preferredPhone"), event.target.value)}>
            <option value="">Select preferred</option>
            {preferredPhoneOptions.map(([value, label]) => (
              <option value={value} key={value}>{label}</option>
            ))}
          </select>
        </label>
        <label className="resident-form-field">
          <span>Fax</span>
          <input type="tel" value={formData[contactField(prefix, "fax")] || ""} onChange={(event) => updateField(contactField(prefix, "fax"), event.target.value)} />
        </label>
        <label className="resident-form-field">
          <span>Email</span>
          <input type="email" value={formData[contactField(prefix, "email")] || ""} onChange={(event) => updateField(contactField(prefix, "email"), event.target.value)} />
        </label>
      </div>
      <ResidentAddressFields
        appConfigLoading={appConfigLoading}
        compact
        formData={formData}
        googleMapsApiKey={googleMapsApiKey}
        prefix={prefix}
        title="Address"
        updateField={updateField}
      />
      <label className="resident-form-field resident-contact-notes-field">
        <span>Notes</span>
        <textarea value={contact.notes || ""} onChange={(event) => updateContact(contact.id, "notes", event.target.value)} placeholder="Optional notes about this contact" rows={2} />
      </label>
    </article>
  );
}

export default function NewResidentPage() {
  const { residentId: onboardingRouteResidentId } = useParams();
  const { user } = useAuth();
  const { selectedWorkspace, selectedWorkspaceId } = useWorkspace();
  const { uploadFile, confirmFileUpload, uploading: filesUploading } = useFileUpload();
  const [currentStep, setCurrentStep] = useState(0);
  const [contacts, setContacts] = useState([]);
  const [otherInsurances, setOtherInsurances] = useState([]);
  const [customCareQuestions, setCustomCareQuestions] = useState([]);
  const [documentFiles, setDocumentFiles] = useState([]);
  const [onboardingStepStatus, setOnboardingStepStatus] = useState(emptyDraft.onboardingStepStatus);
  const [onboardingResidentId, setOnboardingResidentId] = useState("");
  const [onboardingRegistrationId, setOnboardingRegistrationId] = useState("");
  const [assessmentProcessId, setAssessmentProcessId] = useState("");
  const [signedDocumentValidations, setSignedDocumentValidations] = useState(emptyDraft.signedDocumentValidations);
  const [selectedContactId, setSelectedContactId] = useState("");
  const [showSsn, setShowSsn] = useState(false);
  const [assessmentFile, setAssessmentFile] = useState(null);
  const [assessmentImported, setAssessmentImported] = useState(false);
  const [formData, setFormData] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [savingAction, setSavingAction] = useState("");
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState("");
  const [onboardingPreview, setOnboardingPreview] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [cachedAssessmentFileMeta, setCachedAssessmentFileMeta] = useState(null);
  const assessmentFileRef = useRef(null);
  const documentPreviewUrlsRef = useRef(new Set());
  const { appConfig, loading: appConfigLoading } = useAppConfig();
  const googleMapsApiKey =
    appConfig?.googleMapsApiKey ||
    appConfig?.googleMapsApiMatrixKey ||
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
    "";
  const activeStep = intakeSteps[currentStep][0];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === intakeSteps.length - 1;
  const displayedAssessmentFile = assessmentFile || cachedAssessmentFileMeta;
  const actorId = user?.id || "";
  const furthestReachedStep = Math.max(0, Number(onboardingStepStatus?.currentStepIndex) || 0);
  const reachableStep = Math.max(furthestReachedStep, currentStep);
  const progressStepStatusFor = (stepIndex, statusOverride = "") =>
    onboardingStepStatusFor(Math.max(furthestReachedStep, stepIndex), new Date().toISOString(), statusOverride);
  useEffect(() => () => {
    documentPreviewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    documentPreviewUrlsRef.current.clear();
  }, []);
  const saveDraft = useCallback((stepOverride = currentStep, overrides = {}) => {
    const savedAt = new Date();
    const progressIndex = Math.max(
      Number(onboardingStepStatus?.currentStepIndex) || 0,
      Number(overrides.progressStepIndex) || 0,
      stepOverride,
    );
    const nextStepStatus = overrides.onboardingStepStatus || onboardingStepStatusFor(progressIndex, savedAt.toISOString());
    const draftPayload = {
      currentStep: stepOverride,
      contacts,
      otherInsurances,
      customCareQuestions,
      documentFiles: overrides.documentFiles ?? documentFiles,
      onboardingStepStatus: nextStepStatus,
      onboardingResidentId: overrides.onboardingResidentId ?? onboardingResidentId,
      onboardingRegistrationId: overrides.onboardingRegistrationId ?? onboardingRegistrationId,
      assessmentProcessId: overrides.assessmentProcessId ?? assessmentProcessId,
      signedDocumentValidations,
      selectedContactId,
      assessmentFileMeta: assessmentFile
        ? {
            name: assessmentFile.name,
            size: assessmentFile.size,
            type: assessmentFile.type,
          }
        : cachedAssessmentFileMeta,
      assessmentImported,
      formData,
    };
    localStorage.setItem(NEW_RESIDENT_DRAFT_KEY, JSON.stringify(draftPayload));
    setOnboardingStepStatus(nextStepStatus);
    setDraftSavedAt(savedAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }));
  }, [
    assessmentFile,
    assessmentImported,
    cachedAssessmentFileMeta,
    assessmentProcessId,
    contacts,
    currentStep,
    customCareQuestions,
    documentFiles,
    formData,
    onboardingStepStatus,
    otherInsurances,
    onboardingRegistrationId,
    onboardingResidentId,
    selectedContactId,
    signedDocumentValidations,
  ]);
  const resetResidentDraft = () => {
    const confirmed = window.confirm("Clear all New Resident form data and cached draft?");
    if (!confirmed) return;
    localStorage.removeItem(NEW_RESIDENT_DRAFT_KEY);
    setCurrentStep(emptyDraft.currentStep);
    setContacts(emptyDraft.contacts);
    setOtherInsurances(emptyDraft.otherInsurances);
    setCustomCareQuestions(emptyDraft.customCareQuestions);
    setDocumentFiles(emptyDraft.documentFiles);
    setOnboardingStepStatus(emptyDraft.onboardingStepStatus);
    setOnboardingResidentId(emptyDraft.onboardingResidentId);
    setOnboardingRegistrationId(emptyDraft.onboardingRegistrationId);
    setAssessmentProcessId(emptyDraft.assessmentProcessId);
    setSignedDocumentValidations(emptyDraft.signedDocumentValidations);
    setSelectedContactId(emptyDraft.selectedContactId);
    setAssessmentFile(null);
    setCachedAssessmentFileMeta(null);
    setAssessmentImported(emptyDraft.assessmentImported);
    setFormData(emptyDraft.formData);
    setSubmitError("");
    setDraftSavedAt("");
    if (assessmentFileRef.current) {
      assessmentFileRef.current.value = "";
    }
  };

  useEffect(() => {
    if (onboardingRouteResidentId) {
      careService.residents.getOnboardingPreview(onboardingRouteResidentId)
        .then((preview) => {
          const hydrated = hydrateDraftFromPreview(preview);
          const progressIndex = Number(hydrated.onboardingStepStatus?.currentStepIndex) || 1;
          setCurrentStep(progressIndex);
          setContacts(hydrated.contacts);
          setOtherInsurances(hydrated.otherInsurances);
          setCustomCareQuestions(hydrated.customCareQuestions);
          setDocumentFiles(hydrated.documentFiles);
          setOnboardingStepStatus(hydrated.onboardingStepStatus);
          setOnboardingResidentId(hydrated.onboardingResidentId || onboardingRouteResidentId);
          setOnboardingRegistrationId(hydrated.onboardingRegistrationId);
          setAssessmentProcessId(hydrated.assessmentProcessId || "");
          setSignedDocumentValidations(hydrated.signedDocumentValidations.length ? hydrated.signedDocumentValidations : emptyDraft.signedDocumentValidations);
          setSelectedContactId(hydrated.contacts[0]?.id || "");
          setCachedAssessmentFileMeta(hydrated.formData?.assessmentFile || null);
          setAssessmentImported(Boolean(hydrated.formData?.assessmentFile?.imported));
          setFormData(hydrated.formData);
          setOnboardingPreview(preview);
          setSubmitError("");
        })
        .catch((error) => {
          setSubmitError(errorMessageFrom(error));
        })
        .finally(() => setDraftLoaded(true));
      return;
    }
    try {
      const cachedDraft = JSON.parse(localStorage.getItem(NEW_RESIDENT_DRAFT_KEY) || "null");
      if (cachedDraft && typeof cachedDraft === "object") {
        const cachedStatus = cachedDraft.onboardingStepStatus && typeof cachedDraft.onboardingStepStatus === "object"
          ? cachedDraft.onboardingStepStatus
          : onboardingStepStatusFor(Number.isInteger(cachedDraft.currentStep) ? cachedDraft.currentStep : 0);
        const cachedProgressIndex = Number(cachedStatus.currentStepIndex) || 0;
        const cachedCurrentStep = Number.isInteger(cachedDraft.currentStep) ? cachedDraft.currentStep : 0;
        setCurrentStep(Math.min(cachedCurrentStep, cachedProgressIndex));
        setContacts(Array.isArray(cachedDraft.contacts) ? cachedDraft.contacts : []);
        setOtherInsurances(Array.isArray(cachedDraft.otherInsurances) ? cachedDraft.otherInsurances : []);
        setCustomCareQuestions(Array.isArray(cachedDraft.customCareQuestions) ? cachedDraft.customCareQuestions : []);
        setDocumentFiles(Array.isArray(cachedDraft.documentFiles) ? cachedDraft.documentFiles : []);
        setOnboardingStepStatus(cachedStatus);
        setOnboardingResidentId(cachedDraft.onboardingResidentId || "");
        setOnboardingRegistrationId(cachedDraft.onboardingRegistrationId || "");
        setAssessmentProcessId(cachedDraft.assessmentProcessId || "");
        setSignedDocumentValidations(Array.isArray(cachedDraft.signedDocumentValidations) ? cachedDraft.signedDocumentValidations : emptyDraft.signedDocumentValidations);
        setSelectedContactId(cachedDraft.selectedContactId || "");
        setCachedAssessmentFileMeta(cachedDraft.assessmentFileMeta || null);
        setAssessmentImported(Boolean(cachedDraft.assessmentImported));
        setFormData(cachedDraft.formData && typeof cachedDraft.formData === "object" ? cachedDraft.formData : {});
      }
    } catch {
      localStorage.removeItem(NEW_RESIDENT_DRAFT_KEY);
    } finally {
      setDraftLoaded(true);
    }
  }, [onboardingRouteResidentId]);

  useEffect(() => {
    if (!draftLoaded || submitting || currentStep === 0) return;
    const timeout = window.setTimeout(() => {
      saveDraft();
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [
    assessmentFile,
    assessmentImported,
    cachedAssessmentFileMeta,
    contacts,
    currentStep,
    customCareQuestions,
    documentFiles,
    draftLoaded,
    formData,
    otherInsurances,
    saveDraft,
    selectedContactId,
    signedDocumentValidations,
    submitting,
  ]);

  useEffect(() => {
    if (activeStep !== "review" || !onboardingResidentId) {
      setOnboardingPreview(null);
      return undefined;
    }
    let cancelled = false;
    careService.residents.getOnboardingPreview(onboardingResidentId)
      .then((preview) => {
        if (!cancelled) {
          setOnboardingPreview(preview);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setSubmitError(errorMessageFrom(error));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [activeStep, onboardingResidentId]);

  const goNext = () => setCurrentStep((step) => Math.min(step + 1, intakeSteps.length - 1));
  const uploadAndConfirmDocumentFiles = async () => {
    const uploadedDocuments = [];
    for (const document of documentFiles) {
      if (documentHasStorageFile(document)) {
        const fileId = document.fileId || document.storageFileId;
        const shouldConfirm = !document.confirmed && document.storageStatus !== "CONFIRMED";
        const confirmed = shouldConfirm ? await confirmFileUpload(fileId, actorId) : Boolean(document.confirmed || document.storageStatus === "CONFIRMED");
        uploadedDocuments.push({
          ...document,
          file: undefined,
          fileId,
          confirmed,
          needsUpload: false,
          uploaded: true,
          storageStatus: confirmed ? "CONFIRMED" : "UPLOADED_NOT_CONFIRMED",
        });
        continue;
      }
      if (!documentNeedsStorageUpload(document)) {
        uploadedDocuments.push(document);
        continue;
      }
      const upload = await uploadFile(null, document.file, {
        confirmUpload: true,
        fileType: document.type || document.file?.type || "DOCUMENT",
        includeUserId: false,
        preserveOriginal: true,
        purpose: "OTHER",
        skipUserValidation: true,
      });
      uploadedDocuments.push({
        ...document,
        file: undefined,
        fileId: upload?.fileId || upload?.file?.id || null,
        confirmed: Boolean(upload?.confirmed),
        needsUpload: false,
        uploaded: true,
        uploadedAt: new Date().toISOString(),
        storageStatus: upload?.confirmed ? "CONFIRMED" : "UPLOADED_NOT_CONFIRMED",
      });
    }
    setDocumentFiles(uploadedDocuments);
    return uploadedDocuments;
  };
  const saveCurrentStepToApi = async (nextStepStatus) => {
    const nextDocumentFiles = activeStep === "documents" ? await uploadAndConfirmDocumentFiles() : documentFiles;
    const stagePayloads = buildOnboardingStagePayloads({
      assessmentFile,
      assessmentFileMeta: cachedAssessmentFileMeta,
      assessmentImported,
      contacts,
      customCareQuestions,
      documentFiles: nextDocumentFiles,
      formData,
      onboardingResidentId,
      onboardingStepStatus: nextStepStatus,
      otherInsurances,
      selectedWorkspace,
      selectedWorkspaceId,
      signedDocumentValidations,
    });
    if (activeStep === "bio") {
      const response = onboardingResidentId
        ? careService.residents.updateOnboardingBio(onboardingResidentId, stagePayloads.bio)
        : careService.residents.createOnboardingBio(stagePayloads.bio);
      return { response: await response, documentFiles: nextDocumentFiles };
    }
    if (!onboardingResidentId) {
      throw new Error("Save Bio Data first so a resident id can be created before continuing.");
    }
    let response = null;
    if (activeStep === "contacts") {
      response = await careService.residents.saveOnboardingContacts(onboardingResidentId, stagePayloads.contacts);
      return { response, documentFiles: nextDocumentFiles };
    }
    if (activeStep === "careHistory") {
      response = await careService.residents.saveOnboardingCareHistory(onboardingResidentId, stagePayloads.careHistory);
      return { response, documentFiles: nextDocumentFiles };
    }
    if (activeStep === "care") {
      response = await careService.residents.saveOnboardingCareSnapshot(onboardingResidentId, stagePayloads.care);
      return { response, documentFiles: nextDocumentFiles };
    }
    if (activeStep === "documents") {
      response = await careService.residents.saveOnboardingDocuments(onboardingResidentId, stagePayloads.documents);
      const returnedDocumentFiles = mergeReturnedDocumentFiles(nextDocumentFiles, response);
      setDocumentFiles(returnedDocumentFiles);
      return { response, documentFiles: returnedDocumentFiles };
    }
    return { response: null, documentFiles: nextDocumentFiles };
  };
  const saveAndContinue = async () => {
    setSubmitError("");
    setSubmitting(true);
    setSavingAction("continue");
    const nextStep = Math.min(currentStep + 1, intakeSteps.length - 1);
    const nextStepStatus = progressStepStatusFor(nextStep);
    try {
      const { response, documentFiles: savedDocumentFiles } = await saveCurrentStepToApi(nextStepStatus);
      const nextResidentId = response?.residentId || onboardingResidentId;
      const nextRegistrationId = response?.registrationId || onboardingRegistrationId;
      const nextAssessmentProcessId = response?.assessmentProcessId || response?.workflowId || assessmentProcessId;
      setOnboardingResidentId(nextResidentId || "");
      setOnboardingRegistrationId(nextRegistrationId || "");
      setAssessmentProcessId(nextAssessmentProcessId || "");
      setOnboardingStepStatus(response?.onboardingStepStatus || nextStepStatus);
      saveDraft(nextStep, {
        onboardingResidentId: nextResidentId || "",
        onboardingRegistrationId: nextRegistrationId || "",
        assessmentProcessId: nextAssessmentProcessId || "",
        onboardingStepStatus: response?.onboardingStepStatus || nextStepStatus,
        documentFiles: savedDocumentFiles,
        progressStepIndex: Math.max(furthestReachedStep, nextStep),
      });
      setCurrentStep(nextStep);
    } catch (error) {
      setSubmitError(errorMessageFrom(error));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setSubmitting(false);
      setSavingAction("");
    }
  };
  const saveCurrentStep = async () => {
    setSubmitError("");
    setSubmitting(true);
    setSavingAction("save");
    const savedStepStatus = progressStepStatusFor(currentStep);
    try {
      const { response, documentFiles: savedDocumentFiles } = await saveCurrentStepToApi(savedStepStatus);
      const nextResidentId = response?.residentId || onboardingResidentId;
      const nextRegistrationId = response?.registrationId || onboardingRegistrationId;
      const nextAssessmentProcessId = response?.assessmentProcessId || response?.workflowId || assessmentProcessId;
      const nextStepStatus = response?.onboardingStepStatus || savedStepStatus;
      setOnboardingResidentId(nextResidentId || "");
      setOnboardingRegistrationId(nextRegistrationId || "");
      setAssessmentProcessId(nextAssessmentProcessId || "");
      setOnboardingStepStatus(nextStepStatus);
      saveDraft(currentStep, {
        onboardingResidentId: nextResidentId || "",
        onboardingRegistrationId: nextRegistrationId || "",
        assessmentProcessId: nextAssessmentProcessId || "",
        onboardingStepStatus: nextStepStatus,
        documentFiles: savedDocumentFiles,
        progressStepIndex: Math.max(furthestReachedStep, currentStep),
      });
    } catch (error) {
      setSubmitError(errorMessageFrom(error));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setSubmitting(false);
      setSavingAction("");
    }
  };
  const jumpToStep = (index) => {
    if (index > reachableStep) {
      return;
    }
    if (currentStep > 0) {
      saveDraft(index, { progressStepIndex: furthestReachedStep });
    }
    setCurrentStep(index);
  };
  const goBack = () => setCurrentStep((step) => Math.max(step - 1, 0));
  const openAssessmentUploader = () => assessmentFileRef.current?.click();
  const handleAssessmentFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setAssessmentFile(file);
    setCachedAssessmentFileMeta({
      name: file.name,
      size: file.size,
      type: file.type,
    });
    setAssessmentImported(false);
  };
  const removeAssessmentFile = () => {
    setAssessmentFile(null);
    setCachedAssessmentFileMeta(null);
    setAssessmentImported(false);
    if (assessmentFileRef.current) {
      assessmentFileRef.current.value = "";
    }
  };
  const importAssessment = () => {
    if (!displayedAssessmentFile) {
      openAssessmentUploader();
      return;
    }
    setAssessmentImported(true);
  };
  const updateField = (field, value) => setFormData((current) => ({ ...current, [field]: value }));
  const selectedContact = contacts.find((contact) => contact.id === selectedContactId) || contacts[0];
  const addContact = () => {
    const timestamp = Date.now();
    setContacts((current) => {
      const nextNumber = current.length + 1;
      const nextContact = {
        id: `contact-${timestamp}-${nextNumber}`,
        prefix: `residentContact${timestamp}${nextNumber}`,
        type: "",
        notes: "",
      };
      setSelectedContactId(nextContact.id);
      return [
        ...current,
        nextContact,
      ];
    });
  };
  const updateContact = (id, field, value) => {
    setContacts((current) => current.map((contact) => (contact.id === id ? { ...contact, [field]: value } : contact)));
  };
  const removeContact = (id) => {
    setContacts((current) => {
      const next = current.filter((contact) => contact.id !== id);
      if (selectedContactId === id) {
        setSelectedContactId(next[0]?.id || "");
      }
      return next;
    });
  };
  const addOtherInsurance = () => {
    const timestamp = Date.now();
    setOtherInsurances((current) => [
      ...current,
      {
        id: `other-insurance-${timestamp}-${current.length + 1}`,
        provider: "",
        policyNumber: "",
        groupNumber: "",
        notes: "",
      },
    ]);
  };
  const updateOtherInsurance = (id, field, value) => {
    setOtherInsurances((current) => current.map((insurance) => (insurance.id === id ? { ...insurance, [field]: value } : insurance)));
  };
  const removeOtherInsurance = (id) => {
    setOtherInsurances((current) => current.filter((insurance) => insurance.id !== id));
  };
  const addCustomCareQuestion = () => {
    const timestamp = Date.now();
    setCustomCareQuestions((current) => [
      ...current,
      {
        id: `care-question-${timestamp}-${current.length + 1}`,
        question: "",
        response: "",
      },
    ]);
  };
  const updateCustomCareQuestion = (id, field, value) => {
    setCustomCareQuestions((current) => current.map((question) => (question.id === id ? { ...question, [field]: value } : question)));
  };
  const removeCustomCareQuestion = (id) => {
    setCustomCareQuestions((current) => current.filter((question) => question.id !== id));
  };
  const addDocumentFiles = (event) => {
    const files = Array.from(event.target.files || []).map((file) => {
      const localPreviewUrl = URL.createObjectURL(file);
      documentPreviewUrlsRef.current.add(localPreviewUrl);
      return {
        id: `resident-document-${Date.now()}-${file.name}`,
        file,
        fileName: file.name,
        name: file.name,
        description: "",
        type: file.type,
        size: file.size,
        localPreviewUrl,
        previewUrl: localPreviewUrl,
        uploaded: false,
        confirmed: false,
        fileId: null,
        needsUpload: true,
        storageStatus: "PENDING_UPLOAD",
      };
    });
    if (!files.length) return;
    setDocumentFiles((current) => [...current, ...files]);
    event.target.value = "";
  };
  const updateDocumentFile = (id, field, value) => {
    setDocumentFiles((current) => current.map((document) => (document.id === id ? { ...document, [field]: value } : document)));
  };
  const removeDocumentFile = (id) => {
    setDocumentFiles((current) => current.filter((document) => {
      if (document.id === id && document.localPreviewUrl) {
        URL.revokeObjectURL(document.localPreviewUrl);
        documentPreviewUrlsRef.current.delete(document.localPreviewUrl);
      }
      return document.id !== id;
    }));
  };
  const openDocumentPreview = (document) => {
    const url = documentPreviewUrl(document);
    if (!url) return;
    setFilePreview({
      name: document.name || document.fileName || "Document preview",
      type: document.type || document.mimeType || "",
      url,
      isImage: isImageDocument(document),
    });
  };
  const addSignedDocumentValidation = () => {
    const timestamp = Date.now();
    setSignedDocumentValidations((current) => [
      ...current,
      {
        id: `signed-custom-${timestamp}`,
        documentName: "",
        signedVerified: false,
        fileUploaded: false,
        notes: "",
      },
    ]);
  };
  const updateSignedDocumentValidation = (id, field, value) => {
    setSignedDocumentValidations((current) => current.map((document) => (document.id === id ? { ...document, [field]: value } : document)));
  };
  const removeSignedDocumentValidation = (id) => {
    setSignedDocumentValidations((current) => current.filter((document) => document.id !== id));
  };
  const handleSubmitResident = async () => {
    setSubmitError("");
    setSubmitting(true);
    try {
      if (!onboardingResidentId) {
        throw new Error("Save Bio Data first so the resident can be created before submitting.");
      }
      const submittedStatus = onboardingStepStatusFor(currentStep, new Date().toISOString(), "ACTIVE");
      const response = await careService.residents.confirmOnboarding(onboardingResidentId, {
        workspaceId: selectedWorkspaceId || null,
        organizationId: selectedWorkspace?.organizationId || null,
        onboardingStepStatus: submittedStatus,
        payload: {
          residentId: onboardingResidentId,
          registrationId: onboardingRegistrationId || onboardingPreview?.registrationId || null,
          confirmed: true,
          confirmedAt: submittedStatus.lastSavedAt,
          previewSections,
          previewPayload: onboardingPreview?.previewPayload || null,
        },
      });
      const nextAssessmentProcessId = response?.assessmentProcessId || response?.workflowId || "";
      setAssessmentProcessId(nextAssessmentProcessId);
      localStorage.removeItem(NEW_RESIDENT_DRAFT_KEY);
      setOnboardingStepStatus(response?.onboardingStepStatus || submittedStatus);
      setOnboardingPreview((current) => ({
        ...(current || {}),
        residentId: onboardingResidentId,
        registrationId: response?.registrationId || onboardingRegistrationId || current?.registrationId || null,
        assessmentProcessId: nextAssessmentProcessId || current?.assessmentProcessId || current?.workflowId || "",
        workflowId: nextAssessmentProcessId || current?.workflowId || "",
        onboardingStepStatus: response?.onboardingStepStatus || submittedStatus,
        previewPayload: response?.payload || current?.previewPayload || null,
      }));
      setSubmitError("");
      setDraftSavedAt("");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      setSubmitError(errorMessageFrom(error));
      setCurrentStep(intakeSteps.length - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setSubmitting(false);
    }
  };
  const previewValue = (value) => value || "Not added";
  const previewRowIsWide = (label) => /address|notes|description|details|condition|diagnosis|cause|snapshot|care needed|behavior|document/i.test(label || "");
  const previewValueIsRich = (value) => /<\/?[a-z][\s\S]*>/i.test(String(value || ""));
  const previewSections = [
    {
      title: "Personal Bio Data",
      icon: "ti-user-circle",
      tone: "bio",
      rows: [
        ["Full name", [formData.firstName, formData.middleName, formData.lastName].filter(Boolean).join(" ")],
        ["Preferred name", formData.preferredName],
        ["Date of birth", formData.dateOfBirth],
        ["Social Security Number", formData.socialSecurityNumber],
        ["Sex", formData.sex],
        ["Email", formData.email],
        ["Mobile phone", formData.mobilePhone],
        ["Preferred phone", preferredPhoneLabel(formData.preferredPhone)],
      ],
    },
    {
      title: "Address",
      icon: "ti-map-pin",
      tone: "address",
      rows: [
        ["Address type", formData.addressType === "Other" ? formData.addressTypeOther : formData.addressType],
        ["Street address", formData.address],
        ["City", formData.city],
        ["County", formData.county],
        ["State", formData.state],
        ["Country", formData.country],
        ["Zip Code", formData.zip],
      ],
    },
    {
      title: "Coverage and Directives",
      icon: "ti-shield-check",
      tone: "coverage",
      rows: [
        ["Code status", formData.codeStatus],
        ["Advanced directives", formData.advancedDirectives],
        ["Medicare Number", formData.medicareNumber],
        ["Medicaid Number", formData.medicaidNumber],
        ["Other Supplementary Insurance", formData.hasOtherInsurance],
        ...otherInsurances.map((insurance, index) => [
          `Other insurance ${index + 1}`,
          otherInsuranceFields
            .map(([field, label]) => (insurance[field] ? `${label}: ${insurance[field]}` : ""))
            .filter(Boolean)
            .join(" | "),
        ]),
      ],
    },
    {
      title: "Care History",
      icon: "ti-history",
      tone: "history",
      rows: [
        ["Admitted at a hospital", formData.wasAdmittedAtHospital],
        ...admissionDetailsFields.map(([field, label]) => [`Hospital admission - ${label}`, formData[field]]),
        ["Hospital admission - Address", prefixedAddressSummary(formData, "hospital")],
        ["Hospital admission - Condition, diagnosis, and cause", formData.admissionDescription],
        ["Coming from another family home", formData.fromAnotherFamilyHome],
        ...priorFamilyHomeFields.map(([field, label]) => [`Prior family home - ${label}`, formData[field]]),
        ["Prior family home - Address", prefixedAddressSummary(formData, "priorFamilyHome")],
        ["Prior family home - Details", formData.priorFamilyHomeDetails],
        ["Had prior caregiver", formData.hadPriorCaregiver],
        ...priorCaregiverFields.map(([field, label]) => [`Prior caregiver - ${label}`, formData[field]]),
        ["Prior caregiver - Address", prefixedAddressSummary(formData, "priorCaregiver")],
        ["Prior caregiver - Care details", formData.priorCaregiverDetails],
        ["Background notes", formData.bioNotes],
      ],
    },
    {
      title: "Contacts and Decision Makers",
      icon: "ti-address-book",
      tone: "contacts",
      rows: [
        ...contacts.map((contact, index) => [
          `${contact.type || "Contact"} ${index + 1}`,
          [contact.notes ? `Notes: ${contact.notes}` : "", contactSummary(formData, contact.prefix)].filter(Boolean).join(" | "),
        ]),
      ],
    },
    {
      title: "Initial Care Snapshot",
      icon: "ti-heart-rate-monitor",
      tone: "care",
      rows: [
        ["Known diagnosis", formData.diagnosis],
        ["Allergies", formData.allergies],
        ["Medication notes", formData.medicationNotes],
        ["Care needed", formData.careNeeded],
        ["Behavior and safety notes", formData.behaviorSafetyNotes],
        ...customCareQuestions.map((question, index) => [
          question.question || `Custom question ${index + 1}`,
          question.response,
        ]),
      ],
    },
    {
      title: "Documents and Signing",
      icon: "ti-file-check",
      tone: "documents",
      rows: [
        ["Uploaded documents", documentFiles.length ? `${documentFiles.length} file${documentFiles.length === 1 ? "" : "s"} attached` : ""],
        ...documentFiles.map((document, index) => [
          `Document ${index + 1}`,
          [document.name || document.fileName, document.description].filter(Boolean).join(" | "),
        ]),
        ...signedDocumentValidations.map((document) => [
          document.documentName || "Signed document",
          `Signed verified: ${document.signedVerified ? "Yes" : "No"} | File uploaded: ${document.fileUploaded ? "Yes" : "No"}${document.notes ? ` | Notes: ${document.notes}` : ""}`,
        ]),
      ],
    },
  ];
  const residentFullName = [formData.firstName, formData.middleName, formData.lastName].filter(Boolean).join(" ");
  const residentAddressSummary = [formData.address, formData.city, formData.state, formData.zip].filter(Boolean).join(", ");
  const hasSavedBioData = Boolean(onboardingResidentId) || furthestReachedStep >= 1;
  const resumeBioDetails = [
    ["Name", residentFullName || "Not added"],
    ["Preferred", formData.preferredName || "Not added"],
    ["Sex", formData.sex || "Not added"],
    ["Details", residentAddressSummary || formData.email || formData.mobilePhone || "Not added"],
  ];
  const onboardingStatusValue = String(onboardingStepStatus?.status || onboardingPreview?.onboardingStepStatus?.status || "").toUpperCase();
  const residentStatusValue = String(onboardingPreview?.resident?.status || onboardingPreview?.previewPayload?.onboardingStages?.resident?.status || "").toUpperCase();
  const isResidentActive = ["ACTIVE", "CONFIRMED", "ONBOARDED"].includes(onboardingStatusValue) || residentStatusValue === "ACTIVE";
  const activeResidentId = onboardingResidentId || onboardingPreview?.residentId || "";
  const activeAssessmentProcessId = assessmentProcessId || onboardingPreview?.assessmentProcessId || onboardingPreview?.workflowId || "";
  const showActiveResidentLink = Boolean(activeResidentId && ["ACTIVE", "SUBMITTED", "COMPLETED"].includes(onboardingStatusValue));
  const showAssessmentProcessLink = Boolean(activeAssessmentProcessId && ["ACTIVE", "SUBMITTED", "COMPLETED"].includes(onboardingStatusValue));

  return (
    <CareShell className="dashboard-content new-resident-page">
      <div className="crumbs">Residents <i className="ti ti-chevron-right" /> New Resident</div>
      <div className="new-resident-hero">
        <div>
          <span className="section-eyebrow">{isResidentActive ? "Onboarding Details" : "Resident Intake"}</span>
          <h1>{isResidentActive ? "Resident Onboarded Successfully" : "Onboarding New Resident"}</h1>
          <p>
            {isResidentActive
              ? "The resident onboarding requirements are confirmed. No more onboarding action is required."
              : "Enter resident details, contacts, care history, care snapshot, and required documents to complete onboarding."}
          </p>
        </div>
        <div className="new-resident-actions">
          <Link className="mini-btn" to="/residents"><i className="ti ti-arrow-left" /> Residents</Link>
          <button className="mini-btn danger" type="button" onClick={resetResidentDraft}><i className="ti ti-refresh" /> Reset</button>
        </div>
      </div>
      {draftSavedAt ? <div className="resident-draft-status">Draft saved {draftSavedAt}</div> : null}
      {showActiveResidentLink || showAssessmentProcessLink ? (
        <div className="resident-onboarding-assessment-link">
          <span>
            <strong>Resident onboarded successfully</strong>
            <small>No more action is required in the onboarding requirements.</small>
          </span>
          <div className="resident-onboarding-link-actions">
            {activeResidentId ? (
              <Link className="btn primary" to={`/residents/${activeResidentId}`}>
                <i className="ti ti-user-circle" /> Resident Details
              </Link>
            ) : null}
            {showAssessmentProcessLink ? (
              <Link className="btn primary" to={`/assessments/${activeAssessmentProcessId}`}>
                <i className="ti ti-clipboard-text" /> Open Assessment
              </Link>
            ) : null}
          </div>
        </div>
      ) : null}
      {onboardingStepStatus?.canResume ? (
        <div className={`resident-onboarding-status${hasSavedBioData ? " has-bio-summary" : ""}`} role="status">
          <div className="resident-onboarding-status-main">
            <i className="ti ti-player-play" />
            <span>
              <strong>Resume onboarding at {onboardingStepStatus.currentStepLabel}</strong>
              <small>
                Status: {onboardingStepStatus.status.replaceAll("_", " ").toLowerCase()}
                {onboardingStepStatus.lastSavedAt ? ` | Last saved ${new Date(onboardingStepStatus.lastSavedAt).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}` : ""}
              </small>
            </span>
          </div>
          {hasSavedBioData ? (
            <dl className="resident-onboarding-bio-summary" aria-label="Saved resident bio data">
              {resumeBioDetails.map(([label, value]) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
          ) : null}
        </div>
      ) : null}

      <nav className="resident-intake-steps" aria-label="New resident intake steps">
        {intakeSteps.map(([key, title, text], index) => (
          <button
            aria-disabled={index > reachableStep}
            className={`${index === currentStep ? "is-active" : ""}${index < reachableStep ? " is-complete" : ""}${index > reachableStep ? " is-locked" : ""}`}
            disabled={index > reachableStep}
            key={key}
            onClick={() => jumpToStep(index)}
            type="button"
          >
            <b>{index + 1}</b>
            <span><strong>{title}</strong><small>{text}</small></span>
          </button>
        ))}
      </nav>

      {submitError ? (
        <div className="resident-submit-error" role="alert">
          <i className="ti ti-alert-circle" />
          <span>
            <strong>Resident was not submitted</strong>
            <small>{submitError}</small>
          </span>
        </div>
      ) : null}

      {activeStep === "source" && (
        <div className="new-resident-grid">
          <Panel title="Start Manually">
            <div className="resident-start-card">
              <span className="round-icon blue"><i className="ti ti-keyboard" /></span>
              <h3>Manual resident intake</h3>
              <p>Use guided sections to enter bio data, contacts, diagnosis, medication notes, and initial care details yourself.</p>
              <button className="mini-btn" type="button" onClick={goNext}>Enter bio data <i className="ti ti-arrow-right" /></button>
            </div>
          </Panel>

          <Panel title="Import State Assessment">
            <div className="assessment-import-card">
              {!displayedAssessmentFile ? (
                <button className="assessment-upload" type="button" onClick={openAssessmentUploader}>
                  <i className="ti ti-file-upload" />
                  <strong>Upload assessment from state</strong>
                  <span>PDF, DOC, or DOCX assessment file</span>
                </button>
              ) : null}
              <input
                ref={assessmentFileRef}
                className="assessment-file-input"
                type="file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handleAssessmentFile}
              />
              {displayedAssessmentFile ? (
                <div className="assessment-file-selected">
                  <i className={`ti ${assessmentImported ? "ti-circle-check" : "ti-file-check"}`} />
                  <span><strong>{displayedAssessmentFile.name}</strong><small>{assessmentImported ? "Extract successful. Continue to review bio data." : "Attached. Extract data or remove this file."}</small></span>
                  <div className="assessment-file-actions">
                    <button className="mini-btn danger" type="button" onClick={removeAssessmentFile}><i className="ti ti-trash" /> Remove file</button>
                    <button className="mini-btn" type="button" onClick={importAssessment}><i className="ti ti-scan" /> Extract data</button>
                  </div>
                </div>
              ) : null}
              <div className="assessment-import-summary">
                <strong>Information to extract</strong>
                {assessmentItems.map((item) => (
                  <span key={item}><i className="ti ti-check" /> {item}</span>
                ))}
              </div>
              {!displayedAssessmentFile ? (
                <button className="mini-btn full" type="button" onClick={openAssessmentUploader}><i className="ti ti-file-upload" /> Import assessment</button>
              ) : null}
            </div>
          </Panel>
        </div>
      )}

      {activeStep === "bio" && (
        <Panel title="Personal Bio Data">
          <div className="resident-form-sections">
            <div className="resident-form-main-column">
              <section className="resident-form-card">
                <h3>Resident Identifiers</h3>
                <div className="resident-form-grid compact">
                  {bioFields.slice(0, 6).map(([name, label, type]) => (
                    <label className="resident-form-field" key={name}>
                      <span>{label}</span>
                      {name === "socialSecurityNumber" ? (
                        <div className="resident-sensitive-input">
                          <input
                            autoComplete="off"
                            inputMode="numeric"
                            maxLength={11}
                            name={name}
                            type={showSsn ? "text" : "password"}
                            value={formData[name] || ""}
                            onChange={(event) => updateField(name, formatSocialSecurityNumber(event.target.value))}
                          />
                          <button
                            aria-label={showSsn ? "Hide Social Security Number" : "Show Social Security Number"}
                            onClick={() => setShowSsn((current) => !current)}
                            type="button"
                          >
                            <i className={`ti ${showSsn ? "ti-eye-off" : "ti-eye"}`} />
                          </button>
                        </div>
                      ) : (
                        <input name={name} type={type} value={formData[name] || ""} onChange={(event) => updateField(name, event.target.value)} />
                      )}
                    </label>
                  ))}
                  <label className="resident-form-field">
                    <span>Sex</span>
                    <select name="sex" value={formData.sex || ""} onChange={(event) => updateField("sex", event.target.value)}>
                      <option value="">Select sex</option>
                      {sexOptions.map((option) => (
                        <option value={option} key={option}>{option}</option>
                      ))}
                    </select>
                  </label>
                </div>
              </section>

              <section className="resident-form-card">
                <h3>Status</h3>
                <div className="resident-form-grid compact">
                  <label className="resident-form-field">
                    <span>Code Status</span>
                    <select name="codeStatus" value={formData.codeStatus || ""} onChange={(event) => updateField("codeStatus", event.target.value)}>
                      <option value="">Select code status</option>
                      {codeStatusOptions.map((option) => (
                        <option value={option} key={option}>{option}</option>
                      ))}
                    </select>
                  </label>
                  <label className="resident-form-field">
                    <span>Advanced Directives</span>
                    <select name="advancedDirectives" value={formData.advancedDirectives || ""} onChange={(event) => updateField("advancedDirectives", event.target.value)}>
                      <option value="">Select option</option>
                      {yesNoOptions.map((option) => (
                        <option value={option} key={option}>{option}</option>
                      ))}
                    </select>
                  </label>
                </div>
              </section>

            <section className="resident-form-card">
              <h3>Contact and Dates</h3>
              <div className="resident-form-grid compact">
                {phoneFields.map(([name, label]) => (
                  <label className="resident-form-field" key={name}>
                    <span>{label}</span>
                    <input name={name} type="tel" value={formData[name] || ""} onChange={(event) => updateField(name, event.target.value)} />
                  </label>
                ))}
                <label className="resident-form-field">
                  <span>Preferred phone</span>
                  <select name="preferredPhone" value={formData.preferredPhone || ""} onChange={(event) => updateField("preferredPhone", event.target.value)}>
                    <option value="">Select preferred</option>
                    {preferredPhoneOptions.map(([value, label]) => (
                      <option value={value} key={value}>{label}</option>
                    ))}
                  </select>
                </label>
                {bioFields.slice(6).map(([name, label, type]) => (
                  <label className="resident-form-field" key={name}>
                    <span>{label}</span>
                    <input name={name} type={type} value={formData[name] || ""} onChange={(event) => updateField(name, event.target.value)} />
                  </label>
                ))}
              </div>
            </section>

            <section className="resident-form-card">
              <h3>Insurance</h3>
              <div className="resident-form-grid compact">
                {insuranceFields.map(([name, label]) => (
                  <label className="resident-form-field" key={name}>
                    <span>{label}</span>
                    <input name={name} type="text" value={formData[name] || ""} onChange={(event) => updateField(name, event.target.value)} />
                  </label>
                ))}
                <label className="resident-form-field">
                  <span>Do you have other Supplementary Insurance?</span>
                  <select
                    name="hasOtherInsurance"
                    value={formData.hasOtherInsurance || ""}
                    onChange={(event) => {
                      updateField("hasOtherInsurance", event.target.value);
                      if (event.target.value !== "Yes") {
                        setOtherInsurances([]);
                      }
                    }}
                  >
                    <option value="">Select option</option>
                    {yesNoOptions.map((option) => (
                      <option value={option} key={option}>{option}</option>
                    ))}
                  </select>
                </label>
              </div>
              {formData.hasOtherInsurance === "Yes" ? (
                <div className="resident-other-insurance">
                  <div className="resident-other-insurance-head">
                    <span>Other insurance records</span>
                    <button className="mini-btn resident-add-insurance-btn" type="button" onClick={addOtherInsurance}>
                      <i className="ti ti-plus" /> Add Insurance
                    </button>
                  </div>
                  {otherInsurances.length ? (
                    <div className="resident-other-insurance-list">
                      {otherInsurances.map((insurance, index) => (
                        <article className="resident-other-insurance-card" key={insurance.id}>
                          <div className="resident-other-insurance-title">
                            <strong>Other Insurance {index + 1}</strong>
                            <button aria-label="Remove insurance" type="button" onClick={() => removeOtherInsurance(insurance.id)}>
                              <i className="ti ti-trash" />
                            </button>
                          </div>
                          <div className="resident-form-grid resident-other-insurance-fields">
                            {otherInsuranceFields.map(([field, label]) => (
                              <label className={`resident-form-field${field === "notes" ? " full-span" : ""}`} key={field}>
                                <span>{label}</span>
                                {field === "notes" ? (
                                  <textarea
                                    rows={2}
                                    value={insurance[field] || ""}
                                    onChange={(event) => updateOtherInsurance(insurance.id, field, event.target.value)}
                                  />
                                ) : (
                                  <input
                                    type="text"
                                    value={insurance[field] || ""}
                                    onChange={(event) => updateOtherInsurance(insurance.id, field, event.target.value)}
                                  />
                                )}
                              </label>
                            ))}
                          </div>
                        </article>
                      ))}
                    </div>
                  ) : (
                    <div className="resident-inline-empty">No other insurance added yet.</div>
                  )}
                </div>
              ) : null}
            </section>

            <section className="resident-form-card">
              <ResidentAddressFields
                appConfigLoading={appConfigLoading}
                formData={formData}
                googleMapsApiKey={googleMapsApiKey}
                updateField={updateField}
              />
            </section>

            </div>

            <div className="resident-form-notes-column">
              <section className="resident-form-card">
                <h3>Background Notes</h3>
                <CareRichTextEditor
                  height={360}
                  label="Resident background, routines, preferences, and notes"
                  onChange={(value) => updateField("bioNotes", value)}
                  value={formData.bioNotes || ""}
                />
              </section>
              <section className="resident-form-card resident-bio-guidance-card">
                <h3>Form Guidance</h3>
                <ul>
                  <li>Use the left sections for factual resident intake data from assessment files, family, or the resident.</li>
                  <li>Use Care History for admission source, prior care settings, and previous caregiver details.</li>
                  <li>Use Background Notes for routines, preferences, history, risks, and context that helps the care team understand the resident.</li>
                  <li>Keep sensitive identifiers accurate and only reveal masked values when needed.</li>
                </ul>
              </section>
            </div>
          </div>
        </Panel>
      )}

      {activeStep === "contacts" && (
        <Panel title="Contacts and Care Team">
          <div className="resident-contact-toolbar">
            <span>
              <strong>Contacts</strong>
              <small>Add emergency contacts, care coordinators, providers, pharmacy, specialists, and other resident contacts.</small>
            </span>
            <button className="btn primary" type="button" onClick={addContact}>
              <i className="ti ti-plus" /> Add Contact
            </button>
          </div>
          <div className="resident-contact-workspace">
            <aside className="resident-contact-list" aria-label="Added contacts">
              {contacts.length ? contacts.map((contact, index) => {
                const name = formData[contactField(contact.prefix, "name")];
                const phone = phoneValueFor(formData, contact.prefix);
                const preferred = preferredPhoneLabel(formData[contactField(contact.prefix, "preferredPhone")]);
                return (
                  <button
                    className={`resident-contact-list-item${contact.id === selectedContact?.id ? " is-active" : ""}`}
                    key={contact.id}
                    onClick={() => setSelectedContactId(contact.id)}
                    type="button"
                  >
                    <span>
                      <strong>{contact.type || `Contact ${index + 1}`}</strong>
                      <small>{name || contact.notes || "No name added yet"}</small>
                    </span>
                    {phone ? <em>{preferred ? `${preferred}: ${phone}` : phone}</em> : null}
                  </button>
                );
              }) : (
                <div className="resident-contact-empty">
                  <i className="ti ti-address-book" />
                  <strong>No contacts added</strong>
                  <small>Use Add Contact to capture the first relationship or care team contact.</small>
                </div>
              )}
            </aside>
            <div className="resident-contact-editor">
              {selectedContact ? (
                <FacesheetContactCard
                  appConfigLoading={appConfigLoading}
                  canRemove
                  contact={selectedContact}
                  formData={formData}
                  googleMapsApiKey={googleMapsApiKey}
                  key={selectedContact.id}
                  onRemove={removeContact}
                  updateContact={updateContact}
                  updateField={updateField}
                />
              ) : (
                <div className="resident-contact-empty resident-contact-empty-editor">
                  <i className="ti ti-user-plus" />
                  <strong>Select or add a contact</strong>
                  <small>The contact form will appear here after you click Add Contact.</small>
                </div>
              )}
            </div>
          </div>
        </Panel>
      )}

      {activeStep === "careHistory" && (
        <Panel title="Care History">
          <div className="care-history-sections">
            <section className="resident-form-card">
              <h3>Hospital Admission</h3>
              <div className="resident-form-grid compact">
                <label className="resident-form-field full-span">
                  <span>Was the client admitted at a hospital?</span>
                  <select
                    name="wasAdmittedAtHospital"
                    value={formData.wasAdmittedAtHospital || ""}
                    onChange={(event) => updateField("wasAdmittedAtHospital", event.target.value)}
                  >
                    <option value="">Select option</option>
                    {yesNoOptions.map((option) => (
                      <option value={option} key={option}>{option}</option>
                    ))}
                  </select>
                </label>
                {formData.wasAdmittedAtHospital === "Yes" ? (
                  <div className="resident-conditional-fields full-span">
                    <div className="resident-form-section-title">
                      <i className="ti ti-building-hospital" />
                      <span>Admission Details</span>
                    </div>
                    {admissionDetailsFields.map(([name, label, type]) => (
                      <label className="resident-form-field" key={name}>
                        <span>{label}</span>
                        <input name={name} type={type} value={formData[name] || ""} onChange={(event) => updateField(name, event.target.value)} />
                      </label>
                    ))}
                    <ResidentAddressFields
                      appConfigLoading={appConfigLoading}
                      compact
                      formData={formData}
                      googleMapsApiKey={googleMapsApiKey}
                      prefix="hospital"
                      showAddressType={false}
                      title="Hospital Address"
                      updateField={updateField}
                    />
                    <div className="resident-form-field full-span">
                      <CareRichTextEditor
                        height={90}
                        label="Condition, diagnosis, and cause for admission"
                        onChange={(value) => updateField("admissionDescription", value)}
                        placeholder="Capture the resident condition, diagnosis if any, and cause for admission."
                        value={formData.admissionDescription || ""}
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            </section>

            <section className="resident-form-card">
              <h3>Previous Family Home</h3>
              <div className="resident-form-grid compact">
                <label className="resident-form-field full-span">
                  <span>Is the client coming from another family home?</span>
                  <select
                    name="fromAnotherFamilyHome"
                    value={formData.fromAnotherFamilyHome || ""}
                    onChange={(event) => updateField("fromAnotherFamilyHome", event.target.value)}
                  >
                    <option value="">Select option</option>
                    {yesNoOptions.map((option) => (
                      <option value={option} key={option}>{option}</option>
                    ))}
                  </select>
                </label>
                {formData.fromAnotherFamilyHome === "Yes" ? (
                  <div className="resident-conditional-fields full-span">
                    <div className="resident-form-section-title">
                      <i className="ti ti-home-heart" />
                      <span>Previous family home details</span>
                    </div>
                    {priorFamilyHomeFields.map(([name, label]) => (
                      <label className="resident-form-field" key={name}>
                        <span>{label}</span>
                        <input
                          name={name}
                          type={name.includes("Email") ? "email" : name.includes("Phone") ? "tel" : name.includes("Years") || name.includes("Months") ? "number" : "text"}
                          min={name.includes("Years") || name.includes("Months") ? "0" : undefined}
                          value={formData[name] || ""}
                          onChange={(event) => updateField(name, event.target.value)}
                        />
                      </label>
                    ))}
                    <ResidentAddressFields
                      appConfigLoading={appConfigLoading}
                      compact
                      formData={formData}
                      googleMapsApiKey={googleMapsApiKey}
                      prefix="priorFamilyHome"
                      showAddressType={false}
                      title="Family Home Address"
                      updateField={updateField}
                    />
                    <label className="resident-form-field full-span">
                      <span>Details</span>
                      <textarea
                        name="priorFamilyHomeDetails"
                        rows={2}
                        value={formData.priorFamilyHomeDetails || ""}
                        onChange={(event) => updateField("priorFamilyHomeDetails", event.target.value)}
                      />
                    </label>
                  </div>
                ) : null}
              </div>
            </section>

            <section className="resident-form-card">
              <h3>Prior Caregiver</h3>
              <div className="resident-form-grid compact">
                <label className="resident-form-field full-span">
                  <span>Is there anyone who has been taking care of the client?</span>
                  <select
                    name="hadPriorCaregiver"
                    value={formData.hadPriorCaregiver || ""}
                    onChange={(event) => updateField("hadPriorCaregiver", event.target.value)}
                  >
                    <option value="">Select option</option>
                    {yesNoOptions.map((option) => (
                      <option value={option} key={option}>{option}</option>
                    ))}
                  </select>
                </label>
                {formData.hadPriorCaregiver === "Yes" ? (
                  <div className="resident-conditional-fields full-span">
                    <div className="resident-form-section-title">
                      <i className="ti ti-user-heart" />
                      <span>Prior caregiver details</span>
                    </div>
                    {priorCaregiverFields.map(([name, label]) => (
                      <label className="resident-form-field" key={name}>
                        <span>{label}</span>
                        <input
                          name={name}
                          type={name.includes("Email") ? "email" : name.includes("Phone") ? "tel" : "text"}
                          value={formData[name] || ""}
                          onChange={(event) => updateField(name, event.target.value)}
                        />
                      </label>
                    ))}
                    <ResidentAddressFields
                      appConfigLoading={appConfigLoading}
                      compact
                      formData={formData}
                      googleMapsApiKey={googleMapsApiKey}
                      prefix="priorCaregiver"
                      title="Caregiver Address"
                      updateField={updateField}
                    />
                    <label className="resident-form-field full-span">
                      <span>Care details</span>
                      <textarea
                        name="priorCaregiverDetails"
                        rows={2}
                        value={formData.priorCaregiverDetails || ""}
                        onChange={(event) => updateField("priorCaregiverDetails", event.target.value)}
                      />
                    </label>
                  </div>
                ) : null}
              </div>
            </section>
          </div>
        </Panel>
      )}

      {activeStep === "care" && (
        <Panel title="Initial Care Snapshot">
          <div className="initial-care-snapshot-grid">
            <div className="initial-care-snapshot-main">
              <div className="resident-form-field full-span">
                <CareRichTextEditor
                  height={130}
                  label="Diagnosis"
                  onChange={(value) => updateField("diagnosis", value)}
                  value={formData.diagnosis || ""}
                />
              </div>
              <div className="resident-form-field full-span">
                <CareRichTextEditor
                  height={130}
                  label="Allergies"
                  onChange={(value) => updateField("allergies", value)}
                  value={formData.allergies || ""}
                />
              </div>
              <div className="resident-form-field full-span">
                <CareRichTextEditor
                  height={150}
                  label="Current medication notes"
                  onChange={(value) => updateField("medicationNotes", value)}
                  value={formData.medicationNotes || ""}
                />
              </div>
              <div className="resident-form-field full-span">
                <CareRichTextEditor
                  height={150}
                  label="Care needed"
                  onChange={(value) => updateField("careNeeded", value)}
                  value={formData.careNeeded || ""}
                />
              </div>
              <div className="resident-form-field full-span">
                <CareRichTextEditor
                  height={150}
                  label="Behavior and safety notes"
                  onChange={(value) => updateField("behaviorSafetyNotes", value)}
                  value={formData.behaviorSafetyNotes || ""}
                />
              </div>
            </div>
            <aside className="initial-care-custom-questions">
              <div className="resident-contact-toolbar">
                <span>
                  <strong>Custom Questions</strong>
                  <small>Add extra intake questions that are specific to this resident.</small>
                </span>
                <button className="btn primary" type="button" onClick={addCustomCareQuestion}>
                  <i className="ti ti-plus" /> Add Question
                </button>
              </div>
              {customCareQuestions.length ? (
                <div className="care-custom-question-list">
                  {customCareQuestions.map((question, index) => (
                    <article className="care-custom-question-card" key={question.id}>
                      <div className="resident-other-insurance-title">
                        <strong>Question {index + 1}</strong>
                        <button aria-label="Remove question" type="button" onClick={() => removeCustomCareQuestion(question.id)}>
                          <i className="ti ti-trash" />
                        </button>
                      </div>
                      <label className="resident-form-field">
                        <span>Question</span>
                        <input
                          value={question.question}
                          onChange={(event) => updateCustomCareQuestion(question.id, "question", event.target.value)}
                        />
                      </label>
                      <label className="resident-form-field">
                        <span>Response</span>
                        <textarea
                          rows={3}
                          value={question.response}
                          onChange={(event) => updateCustomCareQuestion(question.id, "response", event.target.value)}
                        />
                      </label>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="resident-inline-empty">No custom questions added yet.</div>
              )}
            </aside>
          </div>
        </Panel>
      )}

      {activeStep === "documents" && (
        <Panel title="Documents and Signing">
          <div className="resident-documents-grid">
            <section className="resident-form-card resident-documents-upload-card">
              <div className="resident-contact-toolbar">
                <span>
                  <strong>Upload Documents</strong>
                  <small>Attach policy files, agreements, assessment documents, photos, and supporting care files.</small>
                </span>
              </div>
              <label className="assessment-upload assessment-upload-compact resident-document-upload">
                <input
                  className="assessment-file-input"
                  multiple
                  onChange={addDocumentFiles}
                  type="file"
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg"
                />
                <i className="ti ti-file-upload" />
                <strong>Attach documents</strong>
                <small>PDF, Word, JPG, or PNG files.</small>
              </label>
              {documentFiles.length ? (
                <div className="resident-document-file-list">
                  {documentFiles.map((document) => {
                    const previewUrl = documentPreviewUrl(document);
                    const downloadUrl = documentDownloadUrl(document);
                    const imagePreview = isImageDocument(document);
                    const canPreview = Boolean(previewUrl) && imagePreview;
                    const canDownload = Boolean(downloadUrl);
                    return (
                    <article className="resident-document-file-row" key={document.id}>
                      <button
                        aria-label={imagePreview ? `Preview ${document.fileName || document.name || "document"}` : `Download ${document.fileName || document.name || "document"}`}
                        className="resident-document-preview-thumb"
                        disabled={imagePreview ? !canPreview : !canDownload}
                        onClick={() => {
                          if (imagePreview) {
                            openDocumentPreview(document);
                          } else if (downloadUrl) {
                            window.open(downloadUrl, "_blank", "noopener,noreferrer");
                          }
                        }}
                        type="button"
                      >
                        {imagePreview && previewUrl ? <img src={previewUrl} alt="" /> : <i className="ti ti-download" />}
                      </button>
                      <div className="resident-document-file-fields">
                        <strong>{document.fileName}</strong>
                        <div className="resident-form-grid compact">
                          <label className="resident-form-field">
                            <span>Name / caption</span>
                            <input value={document.name || ""} onChange={(event) => updateDocumentFile(document.id, "name", event.target.value)} />
                          </label>
                          <label className="resident-form-field">
                            <span>Description</span>
                            <input value={document.description || ""} onChange={(event) => updateDocumentFile(document.id, "description", event.target.value)} />
                          </label>
                        </div>
                        <small>
                          {document.type || "File"} {document.size ? `| ${(document.size / 1024).toFixed(1)} KB` : ""}
                          {document.fileId ? ` | File ID: ${document.fileId}` : " | Pending upload"}
                          {document.confirmed ? " | Confirmed" : ""}
                        </small>
                      </div>
                      {imagePreview ? (
                        <button aria-label={`View ${document.fileName}`} disabled={!canPreview} type="button" onClick={() => openDocumentPreview(document)}>
                          <i className="ti ti-eye" />
                        </button>
                      ) : (
                        <a
                          aria-label={`Download ${document.fileName}`}
                          className={`resident-icon-link ${canDownload ? "" : "disabled"}`}
                          href={canDownload ? downloadUrl : undefined}
                          target="_blank"
                          rel="noreferrer"
                          download={document.fileName || document.name || true}
                        >
                          <i className="ti ti-download" />
                        </a>
                      )}
                      <button aria-label={`Remove ${document.fileName}`} type="button" onClick={() => removeDocumentFile(document.id)}>
                        <i className="ti ti-x" />
                      </button>
                    </article>
                    );
                  })}
                </div>
              ) : (
                <div className="resident-inline-empty">No uploaded documents added yet.</div>
              )}
            </section>

            <section className="resident-form-card resident-signed-validation-card">
              <div className="resident-contact-toolbar">
                <span>
                  <strong>Policy / Document Signing Validation</strong>
                  <small>Confirm signed verification and whether the signed file has been uploaded for each policy or document.</small>
                </span>
                <button className="btn primary" type="button" onClick={addSignedDocumentValidation}>
                  <i className="ti ti-plus" /> Add Document
                </button>
              </div>
              <div className="resident-signed-validation-list">
                {signedDocumentValidations.map((document) => (
                  <article className="resident-signed-validation-row" key={document.id}>
                    <label className="resident-form-field resident-signed-document-name">
                      <span>Document / policy</span>
                      <input value={document.documentName || ""} onChange={(event) => updateSignedDocumentValidation(document.id, "documentName", event.target.value)} />
                    </label>
                    <label className="resident-check-card">
                      <input
                        checked={Boolean(document.signedVerified)}
                        onChange={(event) => updateSignedDocumentValidation(document.id, "signedVerified", event.target.checked)}
                        type="checkbox"
                      />
                      <span><strong>Signed verification</strong><small>Signature confirmed</small></span>
                    </label>
                    <label className="resident-check-card">
                      <input
                        checked={Boolean(document.fileUploaded)}
                        onChange={(event) => updateSignedDocumentValidation(document.id, "fileUploaded", event.target.checked)}
                        type="checkbox"
                      />
                      <span><strong>File uploaded</strong><small>Signed file attached</small></span>
                    </label>
                    <label className="resident-form-field resident-signed-notes">
                      <span>Notes</span>
                      <input value={document.notes || ""} onChange={(event) => updateSignedDocumentValidation(document.id, "notes", event.target.value)} />
                    </label>
                    <button aria-label={`Remove ${document.documentName || "document validation"}`} type="button" onClick={() => removeSignedDocumentValidation(document.id)}>
                      <i className="ti ti-trash" />
                    </button>
                  </article>
                ))}
              </div>
            </section>
          </div>
        </Panel>
      )}

      {activeStep === "review" && (
        <Panel title="Review and Submit">
          <div className="resident-onboarding-status resident-review-preview-status" role="status">
            <i className={`ti ${isResidentActive ? "ti-circle-check" : onboardingPreview ? "ti-eye-check" : "ti-eye"}`} />
            <span>
              <strong>
                {isResidentActive
                  ? "Resident onboarded successfully"
                  : onboardingPreview
                    ? "Preview loaded from saved onboarding details"
                    : "Previewing current onboarding details"}
              </strong>
              <small>
                {isResidentActive
                  ? "No more action is required in the onboarding requirements."
                  : onboardingPreview?.registrationId
                  ? `Registration draft ${onboardingPreview.registrationId}`
                  : onboardingResidentId
                    ? "Saved resident details will be confirmed from this step."
                    : "Save Bio Data first to create the resident before confirming."}
              </small>
            </span>
          </div>
          <div className="resident-preview-grid">
            {previewSections.map((section) => (
              <article className={`resident-preview-card tone-${section.tone || "default"}`} key={section.title}>
                <div className="resident-preview-card__head">
                  <span><i className={`ti ${section.icon || "ti-circle-check"}`} /></span>
                  <div>
                    <strong>{section.title}</strong>
                    <small>{section.rows.filter(([, value]) => Boolean(value)).length} completed fields</small>
                  </div>
                </div>
                <dl>
                  {section.rows.map(([label, value]) => (
                    <div className={previewRowIsWide(label) ? "is-wide" : ""} key={label}>
                      <dt>{label}</dt>
                      <dd className={!value ? "is-empty" : ""}>
                        {value && previewValueIsRich(value) ? (
                          <span className="resident-preview-rich" dangerouslySetInnerHTML={{ __html: value }} />
                        ) : (
                          previewValue(value)
                        )}
                      </dd>
                    </div>
                  ))}
                </dl>
              </article>
            ))}
          </div>
          <div className="resident-submit-panel">
            <span>
              <strong>{isResidentActive ? "Resident onboarded successfully" : "Ready to create resident?"}</strong>
              <small>
                {isResidentActive
                  ? "No more action is required. Open the Resident Details page to continue managing care."
                  : "The resident profile will be added to this workspace and can move into assessment, MaG, preliminary care plan, and negotiated care plan flows."}
              </small>
            </span>
            {isResidentActive && activeResidentId ? (
              <Link className="btn primary" to={`/residents/${activeResidentId}`}>
                <i className="ti ti-user-circle" /> Resident Details
              </Link>
            ) : !isResidentActive ? (
              <button className="btn primary" type="button" onClick={handleSubmitResident} disabled={submitting}>
                <i className="ti ti-user-check" /> {submitting ? "Submitting..." : "Submit Resident"}
              </button>
            ) : null}
          </div>
        </Panel>
      )}

      <div className="resident-step-actions">
        <button className="mini-btn" type="button" onClick={goBack} disabled={isFirstStep}><i className="ti ti-arrow-left" /> Back</button>
        <div className="resident-step-action-group">
          {!isLastStep && activeStep !== "source" ? (
            <button className="mini-btn resident-save-step-btn" type="button" onClick={saveCurrentStep} disabled={submitting || filesUploading}>
              <i className="ti ti-device-floppy" /> {savingAction === "save" ? (filesUploading ? "Uploading..." : "Saving...") : "Save"}
            </button>
          ) : null}
          {!isLastStep ? (
            <button
              className="btn primary"
              type="button"
              onClick={activeStep === "source" ? goNext : saveAndContinue}
              disabled={submitting || filesUploading || (activeStep === "source" && displayedAssessmentFile && !assessmentImported)}
            >
              {savingAction === "continue" && activeStep !== "source" ? (filesUploading ? "Uploading..." : "Saving...") : activeStep === "source" ? "Continue" : "Save and Continue"} <i className="ti ti-arrow-right" />
            </button>
          ) : !isResidentActive ? (
            <button className="btn primary" type="button" onClick={handleSubmitResident} disabled={submitting}>
              <i className="ti ti-user-check" /> {submitting ? "Submitting..." : "Submit Resident"}
            </button>
          ) : null}
        </div>
      </div>
      {filePreview ? (
        <div className="resident-file-preview-modal" role="dialog" aria-modal="true" aria-label={filePreview.name}>
          <div className="resident-file-preview-backdrop" onClick={() => setFilePreview(null)} />
          <div className="resident-file-preview-dialog">
            <div className="resident-file-preview-head">
              <span>
                <strong>{filePreview.name}</strong>
                <small>{filePreview.type || "File preview"}</small>
              </span>
              <div>
                <a className="mini-btn" href={filePreview.url} target="_blank" rel="noreferrer"><i className="ti ti-external-link" /> Open</a>
                <button className="mini-btn" type="button" onClick={() => setFilePreview(null)}><i className="ti ti-x" /> Close</button>
              </div>
            </div>
            <div className="resident-file-preview-body">
              {filePreview.isImage ? (
                <img src={filePreview.url} alt={filePreview.name} />
              ) : (
                <iframe src={filePreview.url} title={filePreview.name} />
              )}
            </div>
          </div>
        </div>
      ) : null}
    </CareShell>
  );
}
